/**
 * VoiceOrderModal — dictate a multi-product sales order.
 *
 * Flow:
 *   1. User taps mic and says e.g. "25 kg turmeric and 10 kg chilli for Spice Box Co".
 *   2. Audio → /api/voice/transcribe → text.
 *   3. Text → /api/sales-voice/parse → structured draft {client, items[], rates auto-resolved from client price list or FG default}.
 *   4. User reviews / tweaks the lines (each line shows rate source badge so they
 *      know whether it came from the client's price list, the FG default, or is missing).
 *   5. Create → POST /api/sales/orders (one order per product line — matches the
 *      existing schema; multi-line is faked by sending them sequentially).
 */
import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { X, Sparkles, Check, AlertTriangle, Loader2, Trash2, Plus, Send } from 'lucide-react';
import VoiceInput from './VoiceInput';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const RATE_BADGE = {
  client_price_list: { label: 'Client rate', cls: 'bg-green-900/30 text-green-300 border-green-700' },
  fg_default:        { label: 'FG default', cls: 'bg-blue-900/30 text-blue-300 border-blue-700' },
  missing:           { label: 'Enter rate', cls: 'bg-red-900/30 text-red-300 border-red-700' },
};

export default function VoiceOrderModal({ onClose, onCreated }) {
  const [transcript, setTranscript] = useState('');
  const [parsing, setParsing] = useState(false);
  const [creating, setCreating] = useState(false);
  const [draft, setDraft] = useState(null);   // { client, items[], confidence, notes, raw_transcript }

  // Fire parse when we get a transcript
  const runParse = async (text) => {
    setTranscript(text);
    setParsing(true);
    try {
      const res = await axios.post(`${API}/sales-voice/parse`, { text }, { withCredentials: true });
      setDraft(res.data);
      if (!res.data.client?.matched) {
        toast.warning(`Client "${res.data.client?.name}" not found in your client list — pick one below`);
      }
      const missingRate = (res.data.items || []).filter(i => i.rate === null).length;
      if (missingRate) {
        toast.message(`${missingRate} item(s) have no saved rate — enter them manually`);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not parse');
    } finally {
      setParsing(false);
    }
  };

  // Manual text fallback (tap-type if mic doesn't work)
  const runParseManual = async () => {
    if (!transcript.trim()) { toast.error('Type or speak first'); return; }
    await runParse(transcript);
  };

  const setItem = (i, patch) => setDraft(d => ({
    ...d,
    items: d.items.map((it, idx) => idx === i ? { ...it, ...patch } : it),
  }));
  const removeItem = (i) => setDraft(d => ({ ...d, items: d.items.filter((_, idx) => idx !== i) }));
  const addBlankItem = () => setDraft(d => ({
    ...d,
    items: [...(d?.items || []), { product: '', qty: 1, unit: 'kg', rate: null, rate_source: 'missing', amount: 0, fg_matched: false }],
  }));

  const total = (draft?.items || []).reduce((s, i) => s + ((parseFloat(i.qty) || 0) * (parseFloat(i.rate) || 0)), 0);

  const createOrders = async () => {
    if (!draft) return;
    const clientName = (draft.client?.name || '').trim();
    if (!clientName) { toast.error('Client name required'); return; }
    const validItems = draft.items.filter(i => (i.product || '').trim() && (parseFloat(i.qty) || 0) > 0 && (parseFloat(i.rate) || 0) > 0);
    if (validItems.length === 0) { toast.error('No valid lines to create'); return; }
    if (validItems.length < draft.items.length) {
      if (!window.confirm(`${draft.items.length - validItems.length} incomplete line(s) will be skipped. Continue?`)) return;
    }
    setCreating(true);
    try {
      // iter154 — ONE sales_order with items[] (was: N orders, one per
      // product line). Operator complaint: "if a client orders 3 items
      // I get 3 separate sales orders, not one with 3 lines".
      // Backend POST /api/sales/orders already accepts items[]; we just
      // need to send it correctly.
      await axios.post(`${API}/sales/orders`, {
        client: clientName,
        items: validItems.map(it => ({
          product_id: it.product_id || '',
          product_name: it.product,
          qty: parseFloat(it.qty),
          rate: parseFloat(it.rate),
          unit: it.unit || 'KG',
        })),
        notes: `Created via voice: "${draft.raw_transcript}"`,
      }, { withCredentials: true });
      toast.success(`Order for ${clientName} created with ${validItems.length} item${validItems.length === 1 ? '' : 's'}`);
      onCreated?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to create');
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-zinc-900 border border-zinc-700 rounded-2xl w-full max-w-2xl max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="voice-order-modal">
        <div className="px-5 py-4 border-b border-zinc-800 flex items-center justify-between sticky top-0 bg-zinc-900 z-10">
          <div>
            <h3 className="font-heading font-bold text-zinc-50 text-sm flex items-center gap-2">
              <Sparkles size={14} className="text-purple-400" /> Voice Sales Order
            </h3>
            <p className="text-zinc-500 text-[11px] mt-0.5">
              Speak the order — e.g. <span className="text-amber-300">"25 kg turmeric and 10 kg chilli for Spice Box Co"</span>. Rates auto-fill from client price list.
            </p>
          </div>
          <button onClick={onClose}><X size={18} className="text-zinc-500 hover:text-zinc-200" /></button>
        </div>

        <div className="p-5 space-y-4">
          {/* Voice / text input */}
          <div className="p-3 bg-zinc-950/60 rounded-xl border border-zinc-800 flex items-center gap-2">
            <VoiceInput onTranscript={runParse} context="order" size={14} title="Tap then speak the full order" />
            <input
              value={transcript}
              onChange={(e) => setTranscript(e.target.value)}
              placeholder="Or type: 25 kg turmeric for Spice Box Co"
              className="flex-1 bg-zinc-900 border border-zinc-800 rounded-lg px-3 py-2 text-zinc-100 text-sm focus:outline-none focus:border-amber-500"
              data-testid="voice-order-transcript-input"
              onKeyDown={(e) => { if (e.key === 'Enter' && !parsing) runParseManual(); }}
            />
            <button
              type="button"
              onClick={runParseManual}
              disabled={parsing || !transcript.trim()}
              className="px-3 py-2 rounded-lg bg-purple-500 hover:bg-purple-400 text-white text-xs font-semibold disabled:opacity-50 flex items-center gap-1"
              data-testid="voice-order-parse-btn"
            >
              {parsing ? <Loader2 size={12} className="animate-spin" /> : <Sparkles size={12} />} Parse
            </button>
          </div>

          {parsing && !draft && (
            <div className="text-center text-zinc-500 text-xs py-4 flex items-center justify-center gap-2">
              <Loader2 className="animate-spin" size={14} /> Claude is reading your order…
            </div>
          )}

          {draft && (
            <>
              {/* Client */}
              <div className="flex items-center gap-3 p-3 rounded-xl bg-zinc-950/60 border border-zinc-800">
                <div className="flex-1">
                  <div className="text-[10px] uppercase tracking-wider text-zinc-500">Client</div>
                  <input
                    value={draft.client?.name || ''}
                    onChange={(e) => setDraft(d => ({ ...d, client: { ...d.client, name: e.target.value, matched: false } }))}
                    className="w-full bg-transparent border-0 text-zinc-100 text-sm font-semibold focus:outline-none mt-0.5"
                    data-testid="voice-order-client-input"
                  />
                </div>
                {draft.client?.matched ? (
                  <span className="text-[10px] px-2 py-0.5 rounded-full bg-green-900/30 text-green-300 border border-green-700 flex items-center gap-1">
                    <Check size={10} /> matched
                  </span>
                ) : (
                  <span className="text-[10px] px-2 py-0.5 rounded-full bg-amber-900/30 text-amber-300 border border-amber-700 flex items-center gap-1">
                    <AlertTriangle size={10} /> new client
                  </span>
                )}
              </div>

              {/* Items */}
              <div className="space-y-2">
                <div className="text-[10px] uppercase tracking-wider text-zinc-500">Order Lines ({draft.items.length})</div>
                {draft.items.map((it, i) => {
                  const badge = RATE_BADGE[it.rate_source] || RATE_BADGE.missing;
                  const lineAmt = (parseFloat(it.qty) || 0) * (parseFloat(it.rate) || 0);
                  return (
                    <div key={i} className="p-3 rounded-xl bg-zinc-950/60 border border-zinc-800 space-y-2" data-testid={`voice-order-item-${i}`}>
                      <div className="flex items-center gap-2">
                        <input
                          value={it.product || ''}
                          onChange={(e) => setItem(i, { product: e.target.value })}
                          placeholder="Product"
                          className="flex-1 bg-zinc-900 border border-zinc-800 rounded-lg px-2 py-1.5 text-zinc-50 text-xs focus:outline-none focus:border-amber-500"
                          data-testid={`voice-order-product-${i}`}
                        />
                        <span className={`text-[9px] px-1.5 py-0.5 rounded border whitespace-nowrap ${badge.cls}`}>
                          {badge.label}
                        </span>
                        <button onClick={() => removeItem(i)} className="text-zinc-600 hover:text-red-400 p-1" title="Remove line" data-testid={`voice-order-remove-${i}`}>
                          <Trash2 size={11} />
                        </button>
                      </div>
                      <div className="grid grid-cols-[90px_60px_1fr_100px] gap-2 items-center">
                        <input type="number" step="any" min="0" value={it.qty ?? ''} onChange={(e) => setItem(i, { qty: e.target.value })}
                          placeholder="Qty" className="bg-zinc-900 border border-zinc-800 rounded-lg px-2 py-1.5 text-zinc-50 text-xs focus:outline-none focus:border-amber-500"
                          data-testid={`voice-order-qty-${i}`} />
                        <select value={it.unit || 'kg'} onChange={(e) => setItem(i, { unit: e.target.value })}
                          className="bg-zinc-900 border border-zinc-800 rounded-lg px-1 py-1.5 text-zinc-50 text-xs focus:outline-none">
                          <option value="kg">kg</option><option value="g">g</option><option value="unit">unit</option>
                        </select>
                        <input type="number" step="any" min="0" value={it.rate ?? ''} onChange={(e) => setItem(i, { rate: e.target.value, rate_source: 'missing' })}
                          placeholder="Rate (₹)" className={`border rounded-lg px-2 py-1.5 text-xs focus:outline-none ${
                            it.rate === null ? 'bg-red-900/10 border-red-700 text-red-200' : 'bg-zinc-900 border-zinc-800 text-zinc-50'
                          } focus:border-amber-500`}
                          data-testid={`voice-order-rate-${i}`} />
                        <div className="text-right font-mono text-xs text-amber-300">₹{lineAmt.toFixed(0)}</div>
                      </div>
                    </div>
                  );
                })}
                <button type="button" onClick={addBlankItem} className="w-full text-[11px] text-amber-400 hover:text-amber-300 py-1.5 border border-dashed border-zinc-800 rounded-lg flex items-center justify-center gap-1" data-testid="voice-order-add-line">
                  <Plus size={11} /> Add line manually
                </button>
              </div>

              {/* Total + notes */}
              <div className="flex items-center justify-between px-3 py-2 bg-amber-500/5 border border-amber-500/30 rounded-xl">
                <div className="text-xs text-zinc-300">Total</div>
                <div className="text-sm font-mono font-bold text-amber-300">₹{total.toFixed(2)}</div>
              </div>
              {draft.notes && (
                <div className="text-[11px] text-amber-300 italic bg-amber-500/5 border border-amber-500/20 rounded-lg p-2">
                  {draft.notes}
                </div>
              )}

              <button
                type="button"
                onClick={createOrders}
                disabled={creating}
                className="w-full py-2.5 rounded-xl bg-amber-500 hover:bg-amber-400 text-black font-bold text-sm flex items-center justify-center gap-2 disabled:opacity-50"
                data-testid="voice-order-create-btn"
              >
                <Send size={13} /> {creating ? 'Creating orders…' : `Create ${draft.items.filter(i => i.rate > 0 && i.qty > 0).length} order(s)`}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
